import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './WellnessPhilosophy.css';

const BOUNDARIES = [
  "We don't diagnose conditions, interpret medical or lab results, or recommend medications or supplements.",
  "We're not a therapist, and we won't try to be one. If you're going through something serious, we'll say so and point you toward real professional support.",
  'If something you share suggests you might be in crisis or at risk of harm, our priority shifts immediately to helping you find the right support — not continuing the wellness conversation.',
  "Everything on this page is general, evidence-informed guidance meant to apply broadly. It's not personalized medical advice and doesn't replace a conversation with your doctor, particularly if you have an existing health condition.",
];

const NOVA = [
  {
    label: 'Whole and minimally processed foods',
    body:
      'Fresh produce, eggs, plain yogurt, frozen vegetables, rolled oats — the foundation we encourage as your default. These are foods in or close to their natural state.',
  },
  {
    label: 'Processed foods',
    body:
      'Things with just salt, sugar, or oil added to whole ingredients — cheese, canned beans, bread made from a short ingredient list. Perfectly fine in moderation.',
  },
  {
    label: 'Ultra-processed foods',
    body:
      "Packaged snacks, soda, most fast food — items that have been significantly transformed from their original ingredients. We treat these as occasional rather than routine. We're not asking you to eliminate them, just to notice them and gradually shift the balance.",
  },
];

const STRUCTURE = [
  {
    id: 'goals',
    label: 'Goals',
    body:
      'mosaime organizes coaching around 15 focus areas across three categories — Diet & Nutrition, Mind & Mental Wellness, and Exercise & Movement. Things like eating more whole foods at most meals, prioritizing consistent restful sleep, and finding a way to move that you\'ll actually enjoy. These are the throughlines your coaching connects back to, whether it\'s a meal log, a check-in, or a proactive nudge.',
  },
  {
    id: 'habits',
    label: 'Habits',
    body:
      'For each focus area, mosaime suggests specific recurring habits from a curated list we\'ve built and vetted — not invented on the fly. Things like prepping meals ahead of a busy week, taking a short walk after dinner, or doing a brief breathing exercise. Each habit gets a schedule, a reminder, and tracking, so you can see your consistency build over time through streaks and momentum — not just a vague sense of "doing better."',
  },
  {
    id: 'microhabits',
    label: 'Microhabits',
    body:
      "Alongside tracked habits, mosaime also surfaces lighter, in-the-moment tips — small enough that logging them would be more hassle than help. A two-minute breathing exercise. A glass of water on waking. You can turn any of these into a simple text reminder if it's useful, but we won't ask you to track or report back on them. The point is the nudge, not the record.",
  },
];

function WellnessPhilosophy() {
  return (
    <div className="landing-page">
      <PublicHeader />

      <div className="wp-page">
        <div className="wp-container">

          {/* ── Hero ───────────────────────────────────────────── */}
          <header className="wp-header">
            <h1>Our wellness philosophy.</h1>
            <p className="wp-intro">
              This is the actual framework that shapes every piece of coaching mosaime sends you —
              not marketing copy describing it from a distance. If you've ever wondered "why did
              mosaime say that?", the answer is here.
            </p>
          </header>

          {/* ── Why we built mosaime ───────────────────────────── */}
          <section className="wp-section wp-prose">
            <h2 className="wp-section-title">Why we built mosaime.</h2>
            <p>
              Most wellness tools are built around the assumption that more data means better
              outcomes. Track every calorie. Log every macro. Monitor your sleep efficiency
              percentage. After a few weeks of obsessively tracking all of it, most people burn out
              and quit — not because they stopped caring about their health, but because the tool
              started feeling like a second job.
            </p>
            <p>
              We built mosaime around a different belief: the best wellness tool is the one you'll
              actually keep using. That means low friction (it lives in your texts, not an app you
              have to remember to open), coaching instead of just tracking (it works toward specific
              goals with you, not just logs your data and leaves you to interpret it), and one honest
              picture instead of a scoreboard (diet, movement, and mental health aren't separate
              numbers to optimize — they're intertwined, and we show them that way). We also think
              coaching should be transparent. The framework that shapes every response is published
              here. If our thinking evolves, we'll update this page to match.
            </p>
          </section>

          {/* ── What mosaime is and isn't ──────────────────────── */}
          <section className="wp-section wp-boundaries" aria-label="What mosaime is and isn't">
            <h2 className="wp-section-title">What mosaime is — and isn't.</h2>
            <p className="wp-section-lede">
              mosaime is a wellness coach, not a medical or mental health provider. Concretely:
            </p>
            <ul className="wp-boundaries-list">
              {BOUNDARIES.map(b => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </section>

          {/* ── What we believe ────────────────────────────────── */}
          <section className="wp-section wp-prose">
            <h2 className="wp-section-title">What we believe.</h2>
            <p>
              Sustainable wellbeing comes from the integration of diet, mental wellness, and physical
              activity — not from optimizing one in isolation. We build that integration through
              small, consistent habits rather than big overhauls, because small changes are the ones
              that actually stick.
            </p>
            <p>
              <strong>Progress over perfection</strong> is the idea everything else sits on top of.
              Off days, skipped workouts, and occasional indulgences are a normal part of a healthy
              life — not a failure to correct for. We care about your overall pattern over weeks and
              months, never any single choice. You won't get guilt-tripped by mosaime for a missed
              habit or an imperfect meal, and we actively push back against all-or-nothing thinking,
              because it's one of the most common ways people abandon healthy changes altogether.
            </p>
          </section>

          {/* ── How we think about food ────────────────────────── */}
          <section className="wp-section wp-prose">
            <h2 className="wp-section-title">How we think about food.</h2>
            <p>
              We use the NOVA framework, which groups food by how processed it is rather than by
              counting calories or labeling foods "good" or "bad."
            </p>
            <div className="wp-nova-list">
              {NOVA.map(item => (
                <div key={item.label} className="wp-nova-item">
                  <h3>{item.label}</h3>
                  <p>{item.body}</p>
                </div>
              ))}
            </div>
            <p>
              A few other threads run through our diet guidance: keeping added sugar in check, eating
              in a way that keeps your energy steady rather than spiking and crashing, staying
              hydrated, and eating mindfully — slowing down, noticing hunger and fullness, and
              treating occasional emotional eating as human rather than something to feel bad about.
              Where it's relevant, we'll also nudge toward organic or grass-fed options for foods
              where it matters most, without ever implying you need to spend more to eat well.
            </p>
          </section>

          {/* ── How we think about mind and sleep ──────────────── */}
          <section className="wp-section wp-prose">
            <h2 className="wp-section-title">How we think about mind and sleep.</h2>
            <p>
              Consistent, restful sleep is one of the highest-leverage things you can do for how you
              feel. We'll generally point toward 7–9 hours and a steady wake time as a starting
              target, adjusted to what's realistic for your life. Around that, we care about morning
              light exposure, winding down screens before bed, real social connection, staying
              mentally engaged through learning or creative activity, and having a few go-to tools —
              breathwork, a short walk, journaling — for managing stress as it comes up.
            </p>
            <p>
              Mental health is tracked alongside the physical in mosaime — because it belongs there.
              How you feel on a given day affects what you eat, how you move, and how well you sleep.
              Treating mental wellness as a separate, secondary concern misses how these things
              actually work together.
            </p>
          </section>

          {/* ── How we think about movement ────────────────────── */}
          <section className="wp-section wp-prose">
            <h2 className="wp-section-title">How we think about movement.</h2>
            <p>
              We follow general activity guidelines — roughly 150 minutes of moderate cardio (or 75
              minutes vigorous) per week, plus two or more days of strength work — but we care just
              as much about movement woven into your everyday life: taking the stairs, walking after
              a meal, not sitting for hours at a stretch. Consistency and good form matter far more
              than intensity, especially when you're starting out, and rest and recovery are treated
              as part of the plan, not a deviation from it.
            </p>
            <p>
              You don't need a gym membership for mosaime to be useful. Natural movement — short
              walks, step targets, breaking up long stretches of sitting — counts and is tracked
              independently from structured workouts, because it is independently important.
            </p>
          </section>

          {/* ── How mosaime coaches you ────────────────────────── */}
          <section className="wp-section wp-structure" aria-label="How mosaime coaches you">
            <h2 className="wp-section-title">How mosaime actually coaches you.</h2>
            <p className="wp-section-lede">
              We don't just track what you log — we work with you toward specific, defined goals.
            </p>
            <div className="wp-structure-row">
              {STRUCTURE.map(s => (
                <div key={s.id} className="wp-structure-tile">
                  <span className="wp-structure-label">{s.label}</span>
                  <p>{s.body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── Why we built it this way ───────────────────────── */}
          <section className="wp-section wp-why">
            <h2 className="wp-section-title">Why we built it this way.</h2>
            <p>
              We think the best wellness tool is the one you'll actually keep using — which is why
              mosaime lives in text messages instead of an app you have to remember to open. It's
              also why we don't hand you a dashboard of separate scores: diet, movement, and mental
              wellness aren't separate things, they're intertwined, so we show you one picture
              instead — the Wellness Mosaic — where a few dimmer moments next to brighter ones isn't
              a problem, it's just what balance looks like.
            </p>
            <p>
              We still track the details closely behind the scenes; we just don't think a scoreboard
              is the most honest way to show them to you. And we'd rather be transparent about what
              shapes our coaching than ask you to trust a black box. If our thinking on any of this
              evolves, we'll update this page to match.
            </p>
          </section>

        </div>
      </div>

      {/* ── Footer CTA ───────────────────────────────────────── */}
      <section className="section cta-section">
        <div className="landing-container cta-inner">
          <h2>Ready to put this into practice?</h2>
          <p className="cta-sub">
            mosaime coaches toward everything on this page — starting with wherever you are right now.
          </p>
          <Link to="/signup" className="btn hero-cta">Start Free — 7-Day Trial</Link>
          <p className="cta-secondary-links">
            <Link to="/how-it-works">How it works</Link>
            <span aria-hidden="true"> · </span>
            <Link to="/faq">FAQ</Link>
          </p>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────── */}
      <footer className="landing-footer">
        <div className="landing-container footer-inner">
          <div className="footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img footer-logo-icon-img" />
            <span>mosaime</span>
          </div>
          <div className="footer-links">
            <Link to="/features">Features</Link>
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/philosophy">Philosophy</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/login">Log in</Link>
            <Link to="/signup">Sign up</Link>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} mosaime. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default WellnessPhilosophy;
